import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, CircleArrowLeft, LoaderCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router";

import { PremiumBanner } from "@/components/premium-upsell";
import { ProfileSkeleton } from "@/components/skeleton/profile-skeleton";
import { TournamentClub } from "@/components/tournaments/tournament-club";
import { TournamentInfos } from "@/components/tournaments/tournament-infos";
import { TournamentPlayers } from "@/components/tournaments/tournament-players";
import { TournamentPrizes } from "@/components/tournaments/tournament-prizes";
import { TournamentSpec } from "@/components/tournaments/tournament-spec";
import { Button } from "@/components/ui/button";
import { useMe } from "@/context/user-context";
import { toast } from "@/hooks/use-toast";
import { useTRPC } from "@/lib/trpc";
import { UploadButton } from "@/lib/uploadthing";
import { routes } from "@/routes";

export function TournamentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "tournaments"]);
  const { user } = useMe();
  const location = useLocation();
  const fromPath = location.state?.fromPath;

  if (!id) {
    throw new Error("Tournament ID is required");
  }

  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { data, status } = useQuery(trpc.tournament.getTournament.queryOptions({ tournamentId: id }));
  const { data: club, status: clubStatus } = useQuery(trpc.club.getClub.queryOptions(
    { clubId: data?.clubRepresentativeId ?? "" },
    { enabled: !!data },
  ));
  const { mutate: publishTournament, isPending: isPublishing } = useMutation(trpc.tournament.publishTournament.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournament.getTournament.queryFilter());
      queryClient.invalidateQueries(trpc.tournament.getTournamentList.queryFilter());
    },
  }));

  if (status === "pending" || clubStatus === "pending") {
    return <ProfileSkeleton />;
  }
  if (status !== "success" || clubStatus !== "success") {
    return null;
  }

  // Un brouillon n'est lisible que par son club : c'est ici qu'il l'édite, donc
  // c'est ici qu'il doit voir noir sur blanc qu'il n'est encore visible de personne.
  const isDraft = data.status === "draft";

  return (
    <div className="pb-4 md:container md:mx-auto md:px-6 md:py-4">
      {isDraft && (
        <div className="mb-4 px-2 md:px-0">
          <PremiumBanner
            title={t("tournaments:draft-banner-title")}
            description={user.plan ? t("tournaments:draft-banner-description-paid") : t("tournaments:draft-banner-description")}
            action={user.plan
              ? (
                  <Button className="shrink-0" onClick={() => publishTournament({ tournamentId: data.id })} loading={isPublishing}>
                    {t("tournaments:publish")}
                  </Button>
                )
              : undefined}
          />
        </div>
      )}

      <div className="relative h-[320px] w-full bg-orange-100 md:rounded-[20px]">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="absolute left-1/2 top-4 z-20 -translate-x-1/2"
        >
          <CircleArrowLeft className="text-orange-500" />
          {fromPath === routes.favorites && t("common:favorites-list") }
          {fromPath === routes.tournaments && t("common:tournament-list") }
          {fromPath === routes.profile && t("common:back-to-profile") }
        </Button>

        {data.coverImgUrl && (
          <img
            src={data.coverImgUrl}
            alt={`Photo du ${data.name}`}
            className="pointer-events-none size-full select-none object-cover md:rounded-[20px]"
          />
        )}
        {(user.role === "club") && (
          <UploadButton
            endpoint="tournamentImgUploader"
            onBeforeUploadBegin={(files) => {
              const newF = files.map(
                f => new File([f], `tournament-cover-${data.id}`, { type: f.type }),
              );
              return newF;
            }}
            onClientUploadComplete={() => {
              queryClient.invalidateQueries(trpc.tournament.getTournament.queryFilter());
              toast({
                title: t("common:change-saved"),
                description: t("common:cover-img-success"),
              });
            }}
            onUploadError={(error: Error) => {
              toast({
                title: t("common:change-error"),
                description: (
                  <span>
                    {error.message.includes("FileSizeMismatch") ? t("common:cover-img-error-size") : error.message}
                  </span>
                ),
              });
            }}
            input={{
              tournamentId: data.id,
            }}
            appearance={{
              container: "border border-neutral-400 bg-white w-fit rounded-lg absolute bottom-[4.5rem] left-1/2 -translate-x-1/2 z-30",
              button: "bg-transparent px-2 py-1.5",
              allowedContent: "hidden",
            }}
            content={{
              button({ isUploading }) {
                return (
                  <div className="flex items-center gap-1">
                    {isUploading ? <LoaderCircle className="size-4 animate-spin text-orange-500" /> : <Camera className="size-4 text-orange-500" />}
                    <span className="text-xs text-neutral-500">Image 4MB</span>
                  </div>
                );
              },
            }}
          />
        )}
      </div>

      <div className="bott relative z-20 -mt-16 px-2 sm:px-16 md:px-20 lg:px-32">
        <div className="flex flex-col gap-4">
          <div className="container flex flex-col gap-4 rounded-[20px] bg-white p-2 shadow-sm">
            <TournamentInfos tournament={data} />
            <TournamentPlayers tournament={data} />
            {club && user.role === "player" && <TournamentClub club={club} />}
          </div>
          <TournamentPrizes tournament={data} />
          <TournamentSpec tournament={data} />
        </div>
      </div>
    </div>
  );
}
